import {
    Container,
    ContainerRef,
    IContainer,
    Page
} from "@amzn/ask-legal-domain";
import {
    Alert,
    Button,
    ButtonDropdown,
    ButtonDropdownProps,
    SpaceBetween,
} from "@amzn/awsui-components-react";
import * as React from "react";
import { ContainerViewV2 } from "../container/ContainerViewV2";
import { ContainerEditV2 } from "../container/ContainerEditV2";
import { Element } from "react-scroll";
import { PageDraftModel } from "../../model/page-draft-model";
import { UIModel } from "../../model/ui-model";
import {
    StringUtils,
    getQueryParameterMap,
    isCompareFeatureActivated
} from "../../utils/common-utils";
import "../../styles/util-styles.scss";
import { PageOverviewSectionV2 } from "./PageOverviewSectionV2";
import { PageSection } from "./PageSectionComp";

/**
 * Page sections for the PathFinder Agentic UI lab.
 * - View: live page, and the draft view when the draft is not editable (compare with live).
 * - Edit: the draft editor.
 */
export namespace PageSectionV2 {
    export const Edit = (props: {
        location: Page.SectionLocation;
        updateOrderState: PageDraftModel.ReorderContainerState;
        addContainerState: PageDraftModel.EditContainerState;
        activeEditContainerState: UIModel.State<string>;
        pageBottomRef?: React.RefObject<HTMLDivElement>;
        liveContainerMap?: Map<string, ContainerRef>;
    }) => {
        const [containerNames, setContainerNames] = React.useState<{ id: string, name: string }[]>([]);
        const onLoadComplete = (loadedData: IContainer.LoadContainerOutput) => {
            const containerId = loadedData.containerMetadata.id;
            const containerName = loadedData.containerMetadata.title;
            setContainerNames((prev) => [...prev.filter(c => c.id !== containerId), { id: containerId, name: containerName }]);
        };
        const [showContainerNotFoundAlert, setShowContainerNotFoundAlert] = React.useState<boolean>(false);

        // Check whether the scroll-to container has been deleted already
        React.useEffect(() => {
            const queryParamMap = getQueryParameterMap();
            if (queryParamMap.has("container")) {
                const containerId = queryParamMap.get("container");
                if (!props.updateOrderState.draftField.value.newSections
                        .flatMap(s => s.containers)
                        .find(c => c.id === containerId)) {
                    setShowContainerNotFoundAlert(true);
                }
            }
        }, []);

        const getAddContainerItems = (): ButtonDropdownProps.Items => {
            if (props.location === "Main") {
                return [
                    PageSection.SectionButtonDropdown.AddRichText,
                    PageSection.SectionButtonDropdown.AddAccordion,
                    PageSection.SectionButtonDropdown.AddAccordionWithGroup,
                    PageSection.SectionButtonDropdown.AddCustomList,
                    PageSection.SectionButtonDropdown.AddDocument,
                    PageSection.SectionButtonDropdown.AddDecisionTree,
                    PageSection.SectionButtonDropdown.AddWhosMyLawyer,
                    PageSection.SectionButtonDropdown.AdvancedList
                ];
            }
            if (props.location === "Right") {
                return [PageSection.SectionButtonDropdown.AddCustomList];
            }
            return [];
        };

        const addContainerTypeByButtonId: Record<string, Container.Type> = {
            [PageSection.SectionButtonDropdown.AddRichText.id]: Container.Type.RICH_TEXT,
            [PageSection.SectionButtonDropdown.AddAccordion.id]: Container.Type.ACCORDION,
            [PageSection.SectionButtonDropdown.AddAccordionWithGroup.id]: Container.Type.ACCORDION_GROUP,
            [PageSection.SectionButtonDropdown.AddCustomList.id]: Container.Type.LIST,
            [PageSection.SectionButtonDropdown.AddDocument.id]: Container.Type.DOCUMENT,
            [PageSection.SectionButtonDropdown.AddDecisionTree.id]: Container.Type.DECISION_TREE,
            [PageSection.SectionButtonDropdown.AddWhosMyLawyer.id]: Container.Type.WHOS_MY_LAWYER,
            [PageSection.SectionButtonDropdown.AdvancedList.id]: Container.Type.ADVANCED_LIST
        };

        const onAddContainerClick = (buttonId: string) => {
            props.addContainerState.locationField.setValue(props.location);
            const containerType = addContainerTypeByButtonId[buttonId];
            if (containerType) {
                props.addContainerState.activeContainerTypeField.setValue(containerType);
            }
        };

        const sectionContainers = props.updateOrderState.newOrderedSectionsField.value
            .find(s => s.location === props.location)?.containers ?? [];

        return (
            <div>
                <SpaceBetween size="l">
                    {props.location === "Main" && showContainerNotFoundAlert &&
                        <Alert
                            dismissible
                            type="error"
                            header="Container Not Found"
                            onDismiss={() => setShowContainerNotFoundAlert(false)}>
                            The container you are trying to edit has been removed
                        </Alert>
                    }
                    <ButtonDropdown
                        variant="primary"
                        disabled={!StringUtils.isEmpty(props.addContainerState.activeContainerTypeField.value)}
                        items={getAddContainerItems()}
                        onItemClick={(event) => onAddContainerClick(event.detail.id)}
                    >
                        Add {props.location} Container
                    </ButtonDropdown>
                    {props.location === "Main" && sectionContainers.length > 0 &&
                        <PageOverviewSectionV2
                            state={sectionContainers}
                            containerNames={containerNames}
                        />
                    }
                    {sectionContainers.map(c =>
                        <div key={c.id}>
                            {props.location === "Main" && <>
                                <a id={c.id} />
                                <Element name={c.id} />
                            </>}
                            <ContainerEditV2.Preload
                                id={c.id}
                                updateOrderState={props.updateOrderState}
                                onLoadComplete={props.location === "Main" ? onLoadComplete : undefined}
                                liveContainerRef={props.liveContainerMap?.get(c.id)}
                                activeEditContainerId={props.activeEditContainerState}
                            />
                        </div>
                    )}
                </SpaceBetween>
                {props.location === "Main" &&
                    <div ref={props.pageBottomRef} />
                }
            </div>
        );
    };

    export const View = (props: {
        section: Page.Section;
        editor?: boolean;
        /** Draft review view: shows the compare-with-live toggle on each container */
        draftView?: boolean;
        liveContainerMap?: Map<string, ContainerRef>;
    }) => {
        const [pageControlState, dispatch] = React.useReducer(
            pageControlReducer,
            props.section.containers.map(c => true)
        );
        const [expandCollapseAllTrigger, setExpandCollapseAllTrigger] = React.useState<{ value: string}>();
        const [containerNames, setContainerNames] = React.useState<{ id: string, name: string }[]>([]);
        const onLoadComplete = (loadedData: IContainer.LoadContainerOutput) => {
            const containerId = loadedData.containerMetadata.id;
            const containerName = loadedData.containerMetadata.title;
            setContainerNames((prev) => [...prev.filter(c => c.id !== containerId), { id: containerId, name: containerName }]);
        };

        const getContainersDiffMap = () => {
            if (props.section.location === "Right" || !props.liveContainerMap) return;
            const containersWithDiff = new Map<string, string>();
            const liveContainerIds = Array.from(props.liveContainerMap.keys());
            props.section.containers.forEach(c => {
                if (liveContainerIds.includes(c.id)) {
                    // Modified containers
                    const liveRef = props.liveContainerMap.get(c.id);
                    if (
                        !liveRef ||
                        (c.version !== liveRef.version &&
                            isCompareFeatureActivated(c.containerType))
                    ) containersWithDiff.set(c.id, "UPDATED");
                } else {
                    // New containers
                    if (isCompareFeatureActivated(c.containerType)) containersWithDiff.set(c.id, "INSERTED");
                }
            });
            liveContainerIds.forEach((key) => {
                if (!props.section.containers.map(x => x.id).includes(key)) containersWithDiff.set(key, "DELETED");
            });
            return containersWithDiff;
        };

        const isMain = props.section.location === "Main";

        return (
            <SpaceBetween size="l">
                {isMain && props.section.containers.length > 0 && (
                    <SpaceBetween size="s">
                        <div>
                            {pageControlState.every(v => v === true) ? (
                                <Button
                                    iconName="treeview-collapse" variant="inline-link"
                                    onClick={() => {
                                        dispatch({ type: "collapse-all" });
                                        setExpandCollapseAllTrigger({ value: "collapse" });
                                    }}
                                >Collapse All</Button>
                            ) : (
                                <Button
                                    iconName="treeview-expand" variant="inline-link"
                                    onClick={() => {
                                        dispatch({ type: "expand-all" });
                                        setExpandCollapseAllTrigger({ value: "expand" });
                                    }}
                                >Expand All</Button>
                            )}
                        </div>
                        <PageOverviewSectionV2
                            state={props.section.containers}
                            containerNames={containerNames}
                            containersDiffMap={getContainersDiffMap()}
                        />
                    </SpaceBetween>
                )}
                {props.section.containers.map((c, index) =>
                    <div key={c.id}>
                        <Element name={c.id} />
                        <ContainerViewV2
                            containerRef={c}
                            onLoadComplete={onLoadComplete}
                            location={props.section.location}
                            isExpanded={pageControlState[index]}
                            containerIndex={index}
                            pageControlDispatch={dispatch}
                            showFavoriteButton={isMain}
                            showCompare={!!props.draftView}
                            showEditButton={!!props.editor}
                            liveContainerRef={props.liveContainerMap?.get(c.id)}
                            expandCollapseAllTrigger={expandCollapseAllTrigger}
                        />
                    </div>
                )}
            </SpaceBetween>
        );
    };

    // to control containers expand status
    function pageControlReducer(
        state: boolean[], // state[i] => expand status for container at index i
        params: {
            type: "expand-all" | "collapse-all" | "toggle",
            index?: number
        }
    ): boolean[] {
        switch (params.type) {
            case "expand-all":
                return state.map(() => true);
            case "collapse-all":
                return state.map(() => false);
            case "toggle":
                return state.map((expanded, i) => i === params.index ? !expanded : expanded);
            default:
                throw new Error(`Unexpected type ${params.type}`);
        }
    }
}
